import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import PTTranslate from "../../../../common/components/ui/PTTranslate";
import { ShipAndDock } from "../../../../asset/images/LandingPage";
import { useAuth } from "../../../../common/provider";

const HowItWorks = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { toggleInscriptionDialog } = useAuth();
  
  // Steps data
  const steps = [
    {
      number: 1,
      titleKey: "howItWorks.step1.title",
      descriptionKey: "howItWorks.step1.description"
    },
    {
      number: 2,
      titleKey: "howItWorks.step2.title",
      descriptionKey: "howItWorks.step2.description"
    },
    {
      number: 3,
      titleKey: "howItWorks.step3.title",
      descriptionKey: "howItWorks.step3.description"
    }
  ];

  return (
    <Box
      id="how-it-works" // Added ID for scroll targeting
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: '#f9f9f9',
        width: '100%',
        scrollMarginTop: { xs: '60px', sm: '70px', md: '80px' }, // Added scroll margin to account for fixed navbar
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
              mb: 1.5,
              color: '#1a237e',
              textAlign: 'left',
              lineHeight: 1.2,
            }}
          >
            <PTTranslate tk="howItWorks.title" />
          </Typography>
          
          <Typography
            variant="h3"
            component="h3"
            sx={{
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
              fontWeight: 500,
              color: '#35b37e', // Green color for the subtitle
              textAlign: 'left',
              mb: { xs: 3, md: 4 },
            }}
          >
            <PTTranslate tk="howItWorks.subtitle" />
          </Typography>
        </Box>
        
        {/* Main Content Grid */}
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* Image on the left */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                width: '100%',
                height: { xs: '300px', sm: '400px', md: '500px' },
                borderRadius: 7,
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                component="img"
                src={ShipAndDock}
                alt="Shipping container and dock"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 7,
                }}
              />
            </Box>
          </Grid>
          
          {/* Steps on the right */}
          <Grid item xs={12} md={7}>
            <Box sx={{ pl: { md: 4 } }}>
              {steps.map((step, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 4,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h4"
                    component="h4"
                    sx={{
                      fontSize: { xs: '1.3rem', md: '1.5rem' },
                      fontWeight: 600,
                      mb: 1.5,
                      color: '#232f3e',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box component="span" 
                      sx={{ 
                        fontSize: { xs: '1.3rem', md: '1.5rem' },
                        fontWeight: 700,
                        mr: 1,
                        color: '#232f3e',
                      }}
                    >
                      {`${step.number}. `}
                    </Box>
                    <PTTranslate tk={step.titleKey} />
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.05rem' },
                      color: 'text.primary',
                      lineHeight: 1.6,
                      mb: { xs: 1.5, md: 2 },
                      width: '90%',
                    }}
                  >
                    <PTTranslate tk={step.descriptionKey} />
                  </Typography>
                </Box>
              ))}
              
              {/* Get Started Button */}
              <Box sx={{ mt: { xs: 2, md: 4 } }}>
                <Button
                  onClick={toggleInscriptionDialog}
                  variant="contained"
                  sx={{
                    bgcolor: '#1a237e',
                    color: '#fff',
                    px: { xs: 4, md: 5 },
                    py: { xs: 1.2, md: 1.5 },
                    borderRadius: 7,
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    '&:hover': {
                      bgcolor: '#0d1757',
                    },
                    textTransform: 'none',
                  }}
                >
                  <PTTranslate tk="howItWorks.getStarted" />
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HowItWorks;